import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { getVenueSpendInsights, type VenueInsights } from "@/lib/insights.functions";
import { ReserveHeader } from "@/components/ReserveHeader";

export const Route = createFileRoute("/_authenticated/venue/insights")({
  head: () => ({
    meta: [
      { title: "Spend Insights — The Hospitality Reserve" },
      { name: "description", content: "Monthly spend by role and day of week, with average cost per shift." },
    ],
  }),
  component: VenueInsightsPage,
});

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function fmtGbp(pence: number) {
  return `£${(pence / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function VenueInsightsPage() {
  const fetchInsights = useServerFn(getVenueSpendInsights);
  const [range, setRange] = useState<"90d" | "180d" | "365d">("90d");
  const [data, setData] = useState<VenueInsights | null>(null);

  useEffect(() => {
    const days = range === "90d" ? 90 : range === "180d" ? 180 : 365;
    const from = new Date(Date.now() - days * 86_400_000).toISOString();
    const to = new Date().toISOString();
    fetchInsights({ data: { from, to } }).then(setData);
  }, [range, fetchInsights]);

  const maxMonth = Math.max(1, ...(data?.by_month.map((m) => m.pence) ?? [1]));
  const maxDow = Math.max(1, ...(data?.by_dow.map((d) => d.pence) ?? [1]));

  function downloadCsv() {
    if (!data) return;
    const lines: string[] = [];
    lines.push("Section,Key,Label,Spend (£),Shifts");
    lines.push(`Summary,total,Total spend,${(data.total_pence / 100).toFixed(2)},${data.shifts}`);
    lines.push(`Summary,avg,Average per shift,${(data.avg_per_shift_pence / 100).toFixed(2)},`);
    for (const m of data.by_month) lines.push(`Month,${m.month},${m.month},${(m.pence / 100).toFixed(2)},${m.shifts}`);
    for (const r of data.by_role) lines.push(`Role,${r.role},${r.role},${(r.pence / 100).toFixed(2)},${r.shifts}`);
    for (const d of data.by_dow) lines.push(`DayOfWeek,${d.dow},${DOW[d.dow - 1] ?? d.dow},${(d.pence / 100).toFixed(2)},`);
    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `spend-insights-${range}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }

  function downloadPdf() {
    // Browser print → user picks "Save as PDF"; cleanest no-dependency route.
    window.print();
  }

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="max-w-4xl mx-auto px-4 py-10 space-y-8 print:py-2">
        <header className="flex items-end justify-between flex-wrap gap-3 print:hidden">
          <div>
            <h1 className="text-3xl font-semibold">Spend insights</h1>
            <p className="text-muted-foreground mt-1">Where your shift budget is going.</p>
          </div>
          <div className="flex gap-2 text-sm flex-wrap items-center">
            {(["90d","180d","365d"] as const).map((r) => (
              <button key={r}
                onClick={() => setRange(r)}
                className={`px-3 py-1.5 rounded-md border ${range === r ? "bg-primary text-primary-foreground" : "bg-card"}`}
              >{r === "90d" ? "Last 90 days" : r === "180d" ? "Last 6 months" : "Last 12 months"}</button>
            ))}
            <button onClick={downloadCsv} disabled={!data || data.shifts === 0} className="px-3 py-1.5 rounded-md border bg-card disabled:opacity-50">Export CSV</button>
            <button onClick={downloadPdf} disabled={!data || data.shifts === 0} className="px-3 py-1.5 rounded-md border bg-card disabled:opacity-50">Save as PDF</button>
          </div>
        </header>
        <div className="hidden print:block mb-4">
          <h1 className="text-2xl font-semibold">Spend insights — {range === "90d" ? "Last 90 days" : range === "180d" ? "Last 6 months" : "Last 12 months"}</h1>
          <p className="text-xs text-muted-foreground">Generated {new Date().toLocaleString("en-GB")}</p>
        </div>

        {!data ? <p className="text-muted-foreground">Loading…</p> : data.shifts === 0 ? (
          <p className="text-muted-foreground">No completed shifts in this window yet.</p>
        ) : (
          <>
            <section className="grid sm:grid-cols-3 gap-3">
              <Stat label="Total spend" value={fmtGbp(data.total_pence)} />
              <Stat label="Shifts" value={String(data.shifts)} />
              <Stat label="Avg per shift" value={fmtGbp(data.avg_per_shift_pence)} />
            </section>

            <section className="border rounded-xl p-4 bg-card">
              <h2 className="font-medium mb-3">By month</h2>
              <ul className="space-y-2">
                {data.by_month.map((m) => (
                  <li key={m.month} className="flex items-center gap-3 text-sm">
                    <span className="w-20 text-muted-foreground">{m.month}</span>
                    <div className="flex-1 h-2 bg-muted rounded">
                      <div className="h-2 bg-primary rounded" style={{ width: `${Math.round((m.pence / maxMonth) * 100)}%` }} />
                    </div>
                    <span className="w-24 text-right tabular-nums">{fmtGbp(m.pence)}</span>
                    <span className="w-12 text-right text-muted-foreground">{m.shifts}×</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="grid md:grid-cols-2 gap-4">
              <div className="border rounded-xl p-4 bg-card">
                <h2 className="font-medium mb-3">By role</h2>
                <ul className="space-y-2 text-sm">
                  {data.by_role.map((r) => (
                    <li key={r.role} className="flex justify-between">
                      <span className="capitalize">{r.role.replaceAll("_"," ")}</span>
                      <span className="tabular-nums">{fmtGbp(r.pence)} <span className="text-muted-foreground">({r.shifts})</span></span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border rounded-xl p-4 bg-card">
                <h2 className="font-medium mb-3">By day of week</h2>
                <ul className="space-y-2 text-sm">
                  {Array.from({ length: 7 }, (_, i) => i + 1).map((d) => {
                    const row = data.by_dow.find((x) => x.dow === d);
                    const pence = row?.pence ?? 0;
                    return (
                      <li key={d} className="flex items-center gap-3">
                        <span className="w-10 text-muted-foreground">{DOW[d - 1]}</span>
                        <div className="flex-1 h-2 bg-muted rounded">
                          <div className="h-2 bg-accent rounded" style={{ width: `${Math.round((pence / maxDow) * 100)}%` }} />
                        </div>
                        <span className="w-24 text-right tabular-nums">{fmtGbp(pence)}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="border rounded-xl p-4 bg-card">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-2xl font-semibold mt-1">{value}</p>
    </div>
  );
}
